import * as React from "react"
import { FC, useCallback } from "react"
import * as ImagePicker from "react-native-image-picker"
import MaterialIcons from "react-native-vector-icons/MaterialIcons"
import Resizer from "react-image-file-resizer"
import moment from "moment"
import { Appearance, ListRenderItemInfo, Platform } from "react-native"
import { Button } from "../../button/button"
import { FlatList, Icon, Toast, View } from "native-base"
import { IFlatListProps } from "native-base/lib/typescript/components/basic/FlatList/types"
import { Image } from "../../image/image"
import { JalaIcon } from "../../jala-icon/jala-icon"
import { SettingItem } from "../../setting-item/setting-item"
import { Text } from "../../text/text"
import { color, nativeBaseTheme } from "../../../theme"
import { observer } from "mobx-react-lite"
import { requestCameraAndExternalStoragePermission } from "../../../utils/permissions"
import { translate } from "../../../i18n"
import { useSafeAreaInsets } from "react-native-safe-area-context"

const IMAGE = { width: 98, height: 98 }

export interface MultipleImageInputProps {
  values?: Array<any>
  colorScheme?: "light" | "dark"
  onItemChanged?: (item, index) => void
  flatListProps?: IFlatListProps<any>
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, onItemChanged?: (item, index) => void }> = observer(function Item(props) {
  const { colorScheme = Appearance.getColorScheme(), onItemChanged, info } = props;
  const { item, index } = info;
  const insets = useSafeAreaInsets()

  const onValueChange = useCallback(async (value: "camera"|"image") => {
    const granted = await requestCameraAndExternalStoragePermission()
      .catch(() => false);
    if (!granted) return;

    const callback = (response) => {
      if (response.didCancel) return;
      else if (response.errorMessage) {
        Toast.show({ title: response.errorMessage })
        return;
      }

      const asset = response.assets && response.assets[0]
      if (asset) {
        if (Platform.OS === 'web')
          fetch(asset.uri)
            .then(async (response) => {
              const blob = await response.blob()
              Resizer.imageFileResizer(
                new File([blob], `${asset.fileName || moment().format('x')}.`),
                1024,
                1024,
                'PNG',
                0.8,
                0,
                function(base64) {
                  onItemChanged({
                    ...asset,
                    ...blob,
                    uri: base64,
                  }, index)
                },
                'base64'
              )
            })
        else {
          onItemChanged(asset, index)
        }
      }
    }

    if (value === "camera") {
      await ImagePicker.launchCamera({
        mediaType: 'photo',
        maxHeight: 1024,
        maxWidth: 1024,
        quality: 0.8,
        cameraType: "back"
      }, callback);
    } else {
      await ImagePicker.launchImageLibrary({
        mediaType: "photo",
        maxHeight: 1024,
        maxWidth: 1024,
        quality: 0.8
      }, callback)
    }
  }, [index, onItemChanged])

  const handleDelete = useCallback(() => {
    onItemChanged(null, index)
  }, [index, onItemChanged])

  return <View height="118px">
    <SettingItem
      testID="jala:id/image-button"
      colorScheme={colorScheme}
      type="picker"
      onValueChange={onValueChange}
      inputProps={{
        data: [
          { id: "camera", name: translate('multipleImageInput.camera') },
          { id: "image", name: translate('multipleImageInput.image') },
        ],
        renderItem(info) {
          return <View flexDirection="row" alignItems="center" minHeight="48px">
            <Icon as={MaterialIcons} name={info.item.id} size="18px" mr="8px" />
            <Text
              text={info.item.name}
              colorScheme={colorScheme} isTruncated
            />
          </View>
        }
      }}
      actionsheetProps={{
        actionsheetStyle: { minHeight: (2 * 48) + 32 + insets.bottom }
      }}
    >
      <View
        backgroundColor={color.background}
        size="98px"
        borderRadius="4px"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        {item ? <Image
          source={item}
          resizeMode="cover"
          style={IMAGE}
          {...{alt: item.uri}}
        /> : <Icon as={JalaIcon} name="shrimp" color={color.palette.dust500} size="56px" />}
      </View>
    </SettingItem>
    <View height="20px" alignItems="center" justifyContent="center">
      <Button
        testID="jala:id/image-delete-button"
        tx="delete"
        textPreset="title4"
        preset="link"
        colorScheme={colorScheme}
        textColor={!item ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.danger["500"]}
        onPress={handleDelete}
        disabled={!item}
      />
    </View>
  </View>;
})

const ItemSeparator: FC = observer(function ItemSeparator() {
  return <View size="16px" />
})

/**
 * Describe your component here
 */
export const MultipleImageInput = observer(function MultipleImageInput(props: MultipleImageInputProps) {
  const { colorScheme = Appearance.getColorScheme(), values = [null], onItemChanged, flatListProps } = props

  const renderItem = useCallback((info) => (
    <Item info={info} colorScheme={colorScheme} onItemChanged={onItemChanged} />
  ), [colorScheme, onItemChanged])

  const keyExtractor = useCallback((item, index) => `${(item && item.uri) || index}`, [])

  return (<View>
    <FlatList
      horizontal
      data={values}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      ItemSeparatorComponent={ItemSeparator}
      {...flatListProps}
    />
  </View>)
})
